import React, { useState, useMemo } from "react";
import { GALLERY, GALLERY_CATEGORIES } from "@/mock";
import FinalCTA from "@/components/sections/FinalCTA";

const GalleryPage = () => {
  const [active, setActive] = useState("All");
  const [hovered, setHovered] = useState(null);

  const filtered = useMemo(() => {
    if (active === "All") return GALLERY;
    return GALLERY.filter((g) => g.category === active);
  }, [active]);

  return (
    <div data-testid="gallery-page" className="bg-brand-bg">
      {/* Header */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-6 flex items-center gap-3">
                <span className="block w-10 h-px bg-brand-cta" />
                Gallery
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-brand-ink leading-[0.95]">
                Before and after.<br />
                <span className="italic font-light">Hover to compare.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-base lg:text-lg text-brand-inkSoft leading-relaxed">
                Real homes. Real homeowners. Hover (or tap) any project to see
                where it started.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-brand-border py-6 sticky top-20 z-30 bg-brand-bg/85 backdrop-blur-xl">
        <div
          className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-wrap gap-3"
          data-testid="gallery-filters"
        >
          {GALLERY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActive(cat)}
              data-testid={`filter-${cat.toLowerCase()}`}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] border transition-colors ${
                active === cat
                  ? "bg-brand-ink text-white border-brand-ink"
                  : "bg-transparent text-brand-ink border-brand-border hover:border-brand-ink"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {filtered.length === 0 ? (
            <p className="text-center text-brand-inkSoft py-20">
              No projects in this category yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((g, i) => (
                <article
                  key={g.id}
                  data-testid={`gallery-item-${g.id}`}
                  className="group cursor-pointer"
                  onMouseEnter={() => setHovered(g.id)}
                  onMouseLeave={() => setHovered(null)}
                  onTouchStart={() =>
                    setHovered(hovered === g.id ? null : g.id)
                  }
                >
                  <div
                    className={`relative aspect-[4/5] overflow-hidden bg-brand-ink ${
                      i % 5 === 0 ? "lg:aspect-[4/5]" : ""
                    }`}
                  >
                    <img
                      src={g.before}
                      alt={`${g.title} - before`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        hovered === g.id ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <img
                      src={g.after}
                      alt={`${g.title} - after`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        hovered === g.id ? "opacity-0" : "opacity-100"
                      }`}
                    />

                    {/* State Badge */}
                    <div className="absolute top-4 left-4 z-10">
                      <span
                        className={`inline-block px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] ${
                          hovered === g.id
                            ? "bg-brand-ink/80 text-white backdrop-blur-sm"
                            : "bg-brand-cta text-white"
                        }`}
                      >
                        {hovered === g.id ? "Before" : "After"}
                      </span>
                    </div>
                  </div>
                  <div className="pt-5 flex justify-between items-start gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-cta mb-1">
                        {g.category}
                      </p>
                      <h3 className="font-heading text-lg font-bold tracking-tight text-brand-ink">
                        {g.title}
                      </h3>
                      <p className="text-xs text-brand-inkSoft mt-1">
                        {g.location}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default GalleryPage;
